import React, { useEffect, useState, useMemo } from 'react';
import { monitoringAPI } from '../../services/api';
import socketService from '../../services/socketService';
import { useAuth } from '../../contexts/AuthContext';

const AdminMonitoring = () => {
  const { user } = useAuth();
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingSessionDetails, setLoadingSessionDetails] = useState(false);
  const [webcamFrames, setWebcamFrames] = useState({});
  const [screenFrames, setScreenFrames] = useState({});

  // Fetch active monitoring sessions
  const fetchActiveSessions = async () => {
    try {
      setLoadingSessions(true);
      const res = await monitoringAPI.getActiveSessions();
      const active = res.data?.data?.activeSessions || res.data?.data || [];
      setActiveSessions(Array.isArray(active) ? active : []);
    } catch (err) {
      console.error('Error fetching active monitoring sessions:', err);
      setActiveSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Fetch details for a specific session
  const fetchSessionDetails = async (attemptId) => {
    if (!attemptId) return;
    try {
      setLoadingSessionDetails(true);
      const res = await monitoringAPI.getSession(attemptId);
      const session = res.data?.data?.session || res.data?.data || null;
      setSelectedSession(session);
    } catch (err) {
      console.error('Error fetching monitoring session details:', err);
      setSelectedSession(null);
    } finally {
      setLoadingSessionDetails(false);
    }
  };

  useEffect(() => {
    fetchActiveSessions();
  }, []);

  useEffect(() => {
    if (selectedAttemptId) {
      fetchSessionDetails(selectedAttemptId);
    }
  }, [selectedAttemptId]);

  // Listen for incoming webcam frames from server using custom events
  useEffect(() => {
    const handleWebcamStreamData = (event) => {
      const payload = event.detail;
      console.log('Admin received webcam stream data:', payload);
      
      if (!payload || !payload.attemptId || !payload.stream) {
        console.log('Invalid webcam stream data:', payload);
        return;
      }
      
      setWebcamFrames((prev) => ({
        ...prev,
        [payload.attemptId]: {
          frame: payload.stream,
          userId: payload.userId,
          userName: payload.userName,
          timestamp: payload.timestamp,
        },
      }));
    };

    // Listen for custom events dispatched by socketService
    window.addEventListener('webcam-stream-data', handleWebcamStreamData);

    return () => {
      window.removeEventListener('webcam-stream-data', handleWebcamStreamData);
    };
  }, []);

  // Listen for incoming screen frames from server using custom events
  useEffect(() => {
    const handleScreenStreamData = (event) => {
      const payload = event.detail;
      console.log('Admin received screen stream data:', payload);
      
      if (!payload || !payload.attemptId || !payload.stream) {
        console.log('Invalid screen stream data:', payload);
        return;
      }
      
      setScreenFrames((prev) => ({
        ...prev,
        [payload.attemptId]: {
          frame: payload.stream,
          userId: payload.userId,
          userName: payload.userName,
          timestamp: payload.timestamp,
        },
      }));
    };

    // Listen for custom events dispatched by socketService
    window.addEventListener('screen-stream-data', handleScreenStreamData);

    return () => {
      window.removeEventListener('screen-stream-data', handleScreenStreamData);
    };
  }, []);

  // Listen for monitoring start events
  useEffect(() => {
    const handleMonitoringStarted = (event) => {
      const payload = event.detail;
      console.log('Admin received monitoring started event:', payload);
      
      if (!payload || !payload.attemptId) {
        console.log('Invalid monitoring started event:', payload);
        return;
      }
      
      // Refresh active sessions when monitoring starts
      fetchActiveSessions();
    };

    // Listen for custom events dispatched by socketService
    window.addEventListener('exam-monitoring-started', handleMonitoringStarted);

    return () => {
      window.removeEventListener('exam-monitoring-started', handleMonitoringStarted);
    };
  }, []);

  const currentFrame = useMemo(() => {
    if (!selectedAttemptId) return null;
    return webcamFrames[selectedAttemptId] || null;
  }, [selectedAttemptId, webcamFrames]);
  
  const currentScreenFrame = useMemo(() => {
    if (!selectedAttemptId) return null;
    return screenFrames[selectedAttemptId] || null;
  }, [selectedAttemptId, screenFrames]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 360px) minmax(0, 1fr)',
        gap: 24,
        padding: 24,
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Left: Active sessions list */}
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>Active Monitoring Sessions</h2>
          <button
            type="button"
            onClick={fetchActiveSessions}
            style={{
              border: 'none',
              padding: '6px 10px',
              borderRadius: 999,
              background: '#e5e7eb',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
        {loadingSessions ? (
          <div style={{ padding: 12, fontSize: 14, color: '#6b7280' }}>Loading sessions…</div>
        ) : activeSessions.length === 0 ? (
          <div style={{ padding: 12, fontSize: 14, color: '#9ca3af' }}>No active monitoring sessions.</div>
        ) : (
          <div
            style={{
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            {activeSessions.map((session) => {
              const attemptId = session.attemptId || session.id;
              const isSelected = attemptId === selectedAttemptId;
              return (
                <button
                  key={attemptId}
                  type="button"
                  onClick={() => setSelectedAttemptId(attemptId)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: 'none',
                    marginBottom: 8,
                    background: isSelected ? 'rgba(59,130,246,0.1)' : '#f9fafb',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {session.userName || session.studentName || 'Student'} –{' '}
                    {session.examTitle || session.examName || 'Exam'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Attempt: {attemptId}
                    {session.startedAt && (
                      <> · Started: {new Date(session.startedAt).toLocaleTimeString()}</>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: Live preview + details */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          minHeight: 0,
        }}
      >
        {/* Live webcam preview */}
        <div
          style={{
            background: 'black',
            borderRadius: 16,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.3)',
            padding: 12,
            minHeight: 260,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={{ color: 'white', fontSize: 16, fontWeight: 600 }}>Webcam Preview</div>
            {currentFrame?.timestamp && (
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                Last frame: {new Date(currentFrame.timestamp).toLocaleTimeString()}
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              borderRadius: 12,
              background: '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {selectedAttemptId && currentFrame?.frame ? (
              <img
                src={currentFrame.frame}
                alt="Webcam preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                {selectedAttemptId
                  ? 'Waiting for webcam frames…'
                  : 'Select a session on the left to view webcam preview.'}
              </div>
            )}
          </div>
        </div>
        
        {/* Live screen recording preview */}
        <div
          style={{
            background: 'black',
            borderRadius: 16,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.3)',
            padding: 12,
            minHeight: 260,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={{ color: 'white', fontSize: 16, fontWeight: 600 }}>Screen Recording Preview</div>
            {currentScreenFrame?.timestamp && (
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                Last frame: {new Date(currentScreenFrame.timestamp).toLocaleTimeString()}
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              borderRadius: 12,
              background: '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {selectedAttemptId && currentScreenFrame?.frame ? (
              <img
                src={currentScreenFrame.frame}
                alt="Screen recording preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                {selectedAttemptId
                  ? 'Waiting for screen recording frames…'
                  : 'Select a session on the left to view screen recording preview.'}
              </div>
            )}
          </div>
        </div>

        {/* Session details / events */}
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
            padding: 16,
            minHeight: 160,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Session Details</h3>
          </div>
          {loadingSessionDetails ? (
            <div style={{ padding: 8, fontSize: 14, color: '#6b7280' }}>Loading session details…</div>
          ) : !selectedSession ? (
            <div style={{ padding: 8, fontSize: 14, color: '#9ca3af' }}>
              Select a session on the left to view details and monitoring events.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Student Name:</strong> {selectedSession.userName || selectedSession.studentName || 'N/A'}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Student ID:</strong> {selectedSession.userId || selectedSession.studentId || 'N/A'}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Exam:</strong> {selectedSession.examTitle || selectedSession.examName || 'N/A'}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Exam ID:</strong> {selectedSession.examId || 'N/A'}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Attempt ID:</strong> {selectedSession.attemptId || selectedSession.id}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Status:</strong> {selectedSession.status || 'ACTIVE'}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Started At:</strong> {selectedSession.startedAt ? new Date(selectedSession.startedAt).toLocaleString() : 'N/A'}
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Duration:</strong> {selectedSession.duration || selectedSession.examDuration || 'N/A'} minutes
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Monitoring events</div>
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: 'auto',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {(selectedSession.events || []).length === 0 ? (
                    <div style={{ padding: 8, fontSize: 13, color: '#9ca3af' }}>No events recorded.</div>
                  ) : (
                    (selectedSession.events || []).map((evt, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '6px 8px',
                          fontSize: 12,
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{evt.eventType || evt.type}</span>
                        <span style={{ color: '#6b7280' }}>
                          {evt.createdAt
                            ? new Date(evt.createdAt).toLocaleTimeString()
                            : evt.metadata?.timestamp
                            ? new Date(evt.metadata.timestamp).toLocaleTimeString()
                            : ''}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* All Active Students Grid */}
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
            padding: 16,
            minHeight: 300,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>All Active Students</h3>
            <button
              type="button"
              onClick={fetchActiveSessions}
              style={{
                border: 'none',
                padding: '6px 10px',
                borderRadius: 999,
                background: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
          
          {loadingSessions ? (
            <div style={{ padding: 8, fontSize: 14, color: '#6b7280' }}>Loading students…</div>
          ) : activeSessions.length === 0 ? (
            <div style={{ padding: 8, fontSize: 14, color: '#9ca3af' }}>No active students found.</div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 16,
                maxHeight: 400,
                overflowY: 'auto',
              }}
            >
              {activeSessions.map((session) => {
                const attemptId = session.attemptId || session.id;
                const webcamFrame = webcamFrames[attemptId];
                const screenFrame = screenFrames[attemptId];
                
                return (
                  <div
                    key={attemptId}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: 12,
                      background: '#f9fafb',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                      {session.userName || session.studentName || 'Student'}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                      Exam: {session.examTitle || session.examName || 'N/A'}
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {/* Webcam thumbnail */}
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Webcam</div>
                        <div
                          style={{
                            height: 80,
                            borderRadius: 8,
                            background: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {webcamFrame?.frame ? (
                            <img
                              src={webcamFrame.frame}
                              alt="Webcam thumbnail"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <div style={{ color: '#6b7280', fontSize: 10 }}>
                              No webcam feed
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Screen share thumbnail */}
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Screen</div>
                        <div
                          style={{
                            height: 80,
                            borderRadius: 8,
                            background: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {screenFrame?.frame ? (
                            <img
                              src={screenFrame.frame}
                              alt="Screen share thumbnail"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <div style={{ color: '#6b7280', fontSize: 10 }}>
                              No screen share
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                      Last update: {webcamFrame?.timestamp ? new Date(webcamFrame.timestamp).toLocaleTimeString() : 'N/A'}
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => setSelectedAttemptId(attemptId)}
                      style={{
                        marginTop: 8,
                        width: '100%',
                        padding: '6px',
                        fontSize: 12,
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                      }}
                    >
                      View Details
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMonitoring;